import { BodySemi, CaptionRegular } from '../../Text';
import { DataTable } from '../DataTable';
import { DATA_SOURCES } from '@/config/constants';
import DownloadButton from '../../Button/DownloadButton';
import Drawer from '../../Drawer';
import { IconButton } from '../../Button';
import InstagramRelatedAggregateDrawerContent from '../InstagramRelatedCategoriesTable/InstagramRelatedAggregateDrawerContent';
import RelatedAggregateDrawerContent from './RelatedAggregateDrawerContent';
import { RelatedHashtagsTableHeadColFilter } from '../DataTable/styledComponents';
import SectionTableFilterChips from '../../SectionTableFilterChips';
import Stack from '../../Stack';
import TableHeading from '../../TableHeading';
import { TableStack } from '../RelatedHashtagsTable/styledComponents';
import { createDefaultColumns } from '../DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import { filterNames, filterParentName } from 'utils/filters';
import { sourceOf } from 'utils/dataSources';
import sortDataByKey from 'utils/sortDataByKey';
import toast from 'react-hot-toast';
import {
    useInstagramRelatedCategoryData,
    useInstagramRelatedCategoryGroupData,
    useTikTokRelatedCategoryData,
    useTikTokRelatedCategoryGroupData,
} from 'utils/hooks/api';
import useRelatedAggregateTableProps from './useRelatedAggregateTableProps.hook';

/** Renaming a `tableName` drops the column choices saved under it. */
const BY_SOURCE = {
    [DATA_SOURCES.TIKTOK]: {
        useDataHook: useTikTokRelatedCategoryData,
        useGroupDataHook: useTikTokRelatedCategoryGroupData,
        DrawerContent: RelatedAggregateDrawerContent,
        tooltipKey: 'relatedCategories',
        tableName: 'related-categories',
        contentKey: 'RelatedCategoriesDataTable',
    },
    [DATA_SOURCES.INSTAGRAM]: {
        useDataHook: useInstagramRelatedCategoryData,
        useGroupDataHook: useInstagramRelatedCategoryGroupData,
        DrawerContent: InstagramRelatedAggregateDrawerContent,
        tooltipKey: 'instagramRelatedCategories',
        tableName: 'instagram-related-categories',
        contentKey: 'InstagramRelatedCategoriesDataTable',
    },
};

const relatedCategoryCell = ({ value, row, css = '' }) => (
    <Stack css={css} $space="-.25rem" $direction="column">
        <CaptionRegular as="div" css="max-width: 100%;">
            {row?.relatedName}
        </CaptionRegular>
        <BodySemi as="div" css="text-transform: capitalize;">
            {value}
        </BodySemi>
    </Stack>
);

const defaultGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        ['name', 'parentName', ...visibleOrderedMetrics],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = relatedCategoryCell;
    return columnsConfig;
};

/** Pass `isGroupVariant` for the group rollup variant of the table. */
function RelatedCategoriesSectionShell({
    source,
    affinityGroups,
    brands,
    categories,
    countries,
    week,
    year,
    handleFilter,
    handleSort = sortDataByKey,
    initialSortOption,
    initialFilterOption = {},
    icon,
    title,
    subtitle,
    hideCount = false,
    tools,
    tooltipKey,
    tooltipExample,
    filename = 'related categories',
    name,
    getColumnsConfig = defaultGetColumnsConfig,
    sortParamKey,
    filterParamSeparator,
    filterParamPrefix,
    filtersSlot,
    isGroupVariant = false,
    ...restProps
}) {
    const spec = BY_SOURCE[source];
    const { parentNameFilterKey, volumeKey } = sourceOf(source) || {};

    const resolvedHandleFilter = handleFilter ?? {
        [parentNameFilterKey]: filterParentName,
        name: filterNames,
    };
    const resolvedSortOption = initialSortOption ?? {
        sortBy: volumeKey,
        ascending: false,
    };
    const resolvedTooltipKey = tooltipKey ?? spec.tooltipKey;

    // Selects a hook, so it must not change on a mounted instance.
    const useDataHook = isGroupVariant
        ? spec.useGroupDataHook
        : spec.useDataHook;

    const { query: countQuery } = useDataHook({
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        week,
        year,
        per_page: 1,
        enabled: !!(week && year),
    });
    const totalRecords = countQuery.isPreviousData
        ? null
        : (countQuery.data?.pages?.[0]?.total_records ?? null);

    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
    } = useRelatedAggregateTableProps({
        // Must stay constant per mounted table: it selects a hook inside.
        source,
        useDataHook,
        affinityGroups,
        brands,
        categories,
        countries,
        week,
        year,
        handleFilter: resolvedHandleFilter,
        handleSort,
        initialSortOption: resolvedSortOption,
        initialFilterOption,
        name,
        getColumnsConfig,
        sortParamKey,
        filterParamSeparator,
        filterParamPrefix,
        tableName: isGroupVariant ? `${spec.tableName}-group` : spec.tableName,
        supportsAffinityGroupFilter: !isGroupVariant,
    });

    const defaultFiltersSlot = (
        <SectionTableFilterChips
            handleFilter={resolvedHandleFilter}
            filters={filters}
            setFilter={setFilter}
            setFilters={setFilters}
            queryData={queryData}
        />
    );
    const resolvedFiltersSlot =
        typeof filtersSlot === 'function'
            ? filtersSlot({
                  handleFilter: resolvedHandleFilter,
                  filters,
                  setFilter,
                  setFilters,
                  queryData,
              })
            : filtersSlot !== undefined
              ? filtersSlot
              : defaultFiltersSlot;

    const downloadButton = (
        <DownloadButton
            ButtonComponent={title ? undefined : IconButton}
            size={title ? undefined : 'default'}
            disabled={!dataReady}
            isLoading={isLoading}
            onClick={() => {
                toast.promise(
                    (async () => {
                        const columnsConfigCopy = visibleColumnsConfig.map(
                            (column) => ({ ...column }),
                        );
                        const allData = await getQueryData();
                        downloadItemsAsCSV(
                            filename,
                            allData,
                            columnsConfigCopy,
                        );
                    })(),
                    {
                        loading: 'Preparing download...',
                        success: 'Download complete!',
                        error: 'Error downloading CSV',
                    },
                );
            }}
            mixpanelMeta={{
                contentKey: spec.contentKey,
            }}
        />
    );

    const hasHeading = !!(title || subtitle || icon);
    const StackWrapper = hasHeading ? TableStack : Stack;
    const { DrawerContent } = spec;
    return (
        <>
            {hasHeading ? (
                <TableHeading
                    icon={icon}
                    title={title}
                    subtitle={subtitle}
                    {...(!hideCount && { count: totalRecords })}
                    tooltipKey={resolvedTooltipKey}
                    tooltipExample={tooltipExample}
                    tools={
                        <>
                            {tools}
                            {downloadButton}
                        </>
                    }
                />
            ) : null}
            <StackWrapper $space={(theme) => `${theme.space_9}px`}>
                <div
                    css={`
                        display: flex;
                        flex-wrap: nowrap;
                        gap: 0.75rem;
                        align-items: flex-start;
                    `}
                >
                    <div css="flex-grow: 1;">{resolvedFiltersSlot}</div>
                    {!title && !subtitle && !icon ? (
                        <Stack
                            $direction="row"
                            css={`
                                display: flex;
                                flex-shrink: 0;
                                gap: 0.75rem;
                            `}
                        >
                            {downloadButton}
                            {columnsMenu}
                        </Stack>
                    ) : (
                        columnsMenu
                    )}
                </div>
                <DataTable
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={sortedData}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    noDataMessage={noDataMessage}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    onSearchInputChange={handleSearchInputChange}
                    limitSearchWords={3}
                    DrawerComponent={Drawer}
                    TableHeadColFilter={RelatedHashtagsTableHeadColFilter}
                    columns={visibleColumnsConfig}
                    week={week}
                    year={year}
                    rowHeight={60}
                    renderDrawerContent={(selectedId) => {
                        const selectedRow = selectedId
                            ? rows.find(({ id }) => id === selectedId)
                            : null;
                        if (!selectedRow) return null;

                        const parentEntityIds = brands
                            ? { brands: [selectedRow.brandId] }
                            : { categories: [selectedRow.categoryId] };

                        return (
                            <DrawerContent
                                entity="category"
                                selectedRow={selectedRow}
                                week={week}
                                year={year}
                                countries={
                                    selectedRow.countryId
                                        ? [selectedRow.countryId]
                                        : null
                                }
                                relatedCategories={
                                    selectedRow.relatedCategoryId
                                        ? [selectedRow.relatedCategoryId]
                                        : null
                                }
                                {...parentEntityIds}
                                name={`${name}-${selectedRow.name || ''}`}
                                parentName={name}
                            />
                        );
                    }}
                    {...restProps}
                />
            </StackWrapper>
        </>
    );
}

export default RelatedCategoriesSectionShell;
